import { z } from 'zod';
import { versionTagSchema } from './versioning.js';

/**
 * The ambiance assets (immersion spec §6, decisions I3/I4/I9).
 *
 * The hybrid régime in one line: the MEANING lives in git (the script, the
 * cues, the decors - versioned bricks), the TASTE lives in the base (which
 * file plays behind each cue). Same régime as the cycle thresholds (C9) and
 * the flotte (P1): uploaded and assigned from an admin screen, journalled with
 * the immersive session count at the change, dated rather than frozen.
 *
 * What that costs is real: the exact experience played is no longer entirely
 * in git, and the reader of a report has to read the asset journal - which is
 * why the journal is DISPLAYED in the cycle report, not merely stored.
 */

export const ambianceAssetKindSchema = z.enum(['sound', 'image']);
export type AmbianceAssetKind = z.infer<typeof ambianceAssetKindSchema>;

/**
 * The accepted formats and their ceilings (immersion spec, errors table).
 *
 * Raised from 5 Mo to 100 Mo (issue #122, decision of 2026-09-22, after Alice
 * asked for the impact first): a file used as a background loads ONCE per
 * page, as a CSS `background-image` - never an `<img>` - served with an
 * immutable cache header (`routes/ambiance.ts`), and is never re-fetched
 * during a conversation, because the conversation renders OVER the reader,
 * which stays mounted between turns. A slow or heavy background does not
 * block the interview from becoming interactive either: a CSS background
 * paints independently of layout and script.
 *
 * What did NOT change, and still costs something: nothing compresses or
 * resizes a file on the way in (`ambiance/metadata.ts` only reads headers,
 * never transcodes), so the number below is exactly what an interviewee's
 * phone downloads on the first paint of the page that carries it. Compressing
 * before upload stays the operator's own job.
 *
 * ONE table read by both sides of the wire: the backend refuses from it, the
 * screen announces it. Two spellings of a ceiling would be two ceilings.
 */
export const AMBIANCE_ASSET_RULES = {
  sound: { formats: ['mp3', 'ogg'], maxBytes: 100 * 1024 * 1024, maxLabel: '100 Mo' },
  image: {
    formats: ['webp', 'jpg', 'png', 'gif', 'avif'],
    maxBytes: 100 * 1024 * 1024,
    maxLabel: '100 Mo',
  },
} as const satisfies Record<
  AmbianceAssetKind,
  { formats: readonly string[]; maxBytes: number; maxLabel: string }
>;

/**
 * `gif`, `avif` and the 5 Mo ceiling are H31 (refonte modulaire §9.2, §16),
 * confirmed by the lot that first puts an image on a page: a page of an
 * experience shows illustrations an operator picks, and the two formats a
 * designer actually exports - an animated gif, an avif half the weight of a
 * jpg - had no reason to be refused. The ceiling rises from 3 Mo to 5 Mo and
 * joins the sound's, so there is ONE number to say out loud.
 *
 * **SVG stays out, and that is the point of the list being closed** (H31): an
 * SVG is a document that can carry a script, and this library's files are
 * served from an opaque id to the browser of a person being interviewed.
 */
export const ambianceAssetFormatSchema = z.enum([
  'mp3',
  'ogg',
  'webp',
  'jpg',
  'png',
  'gif',
  'avif',
]);
export type AmbianceAssetFormat = z.infer<typeof ambianceAssetFormatSchema>;

/**
 * The canonical format off an original filename; null when unsupported. ONE
 * spelling read by both sides of the wire, like `AMBIANCE_ASSET_RULES`: the
 * backend derives the stored format from it, and the upload form derives from
 * it which kind the picked file falls under - two spellings would put the two
 * in disagreement on the one file the operator picked.
 *
 * `png` joined the two prior image formats in BUILD-891 (an operator's own
 * words: « j'aimerais aussi du png pour les logo »).
 */
export function ambianceFormatOf(filename: string): AmbianceAssetFormat | null {
  const extension = filename.toLowerCase().split('.').pop() ?? '';
  const canonical = extension === 'jpeg' ? 'jpg' : extension;
  return canonical === 'mp3' ||
    canonical === 'ogg' ||
    canonical === 'webp' ||
    canonical === 'jpg' ||
    canonical === 'png' ||
    canonical === 'gif' ||
    canonical === 'avif'
    ? canonical
    : null;
}

/** Which kind a format belongs to - the format decides, the caller never says. */
export const AMBIANCE_KIND_OF_FORMAT: Record<AmbianceAssetFormat, AmbianceAssetKind> = {
  mp3: 'sound',
  ogg: 'sound',
  webp: 'image',
  jpg: 'image',
  png: 'image',
  gif: 'image',
  avif: 'image',
};

/**
 * One uploaded file. The `id` is OPAQUE and is what the browser is served -
 * never a path, never the original filename.
 *
 * The license is FACULTATIVE on both natures (BUILD-891, decision of
 * 2026-08-24 - it lifts the BUILD-831 asymmetry that kept it mandatory for a
 * sound). An operator's own words: requiring a provenance for every sound
 * turned the door into a word typed to get past it, producing no real
 * provenance and costing a gesture. The field stays, offered and never
 * exigé. Hence `z.string()` and not `.min(1)`: a file with nothing to declare
 * stores the EMPTY string, one spelling of "none" - a nullable column beside a
 * non-empty string would be two (BUILD-810).
 *
 * `width`/`height`/`durationMs` are read off the file's own header at upload
 * (`ambiance/metadata.ts`) and are NULLABLE by construction: a header we
 * cannot parse costs a piece of metadata, never the upload. `tags` are the
 * operator's own words, defaulted rather than optional so every reader sees an
 * array - the régime of BUILD-810 for a shape that is already on a disk.
 */
export const ambianceAssetSchema = z
  .object({
    id: z.string().min(1),
    kind: ambianceAssetKindSchema,
    format: ambianceAssetFormatSchema,
    /** The original filename, for the admin screen only. */
    filename: z.string().min(1),
    sha256: z.string().min(1),
    /** e.g. 'CC0 - freesound.org/123456'. Mandatory for a sound, optional for an image. */
    license: z.string(),
    uploadedAt: z.string().datetime(),
    /** Pixels, off the file's own header; null when the header said nothing. */
    width: z.number().int().positive().nullable().default(null),
    height: z.number().int().positive().nullable().default(null),
    /** Milliseconds, off the file's own header; null when undeterminable. */
    durationMs: z.number().int().nonnegative().nullable().default(null),
    /** Free operator words, for finding a file again. */
    tags: z.array(z.string().min(1)).default([]),
  })
  .strict();
export type AmbianceAsset = z.infer<typeof ambianceAssetSchema>;

/** What `GET /api/ambiance/assets` narrows on: free words, and a kind. */
export const ambianceAssetQuerySchema = z
  .object({
    /** Matched against filename, tags and license, accent- and case-blind. */
    q: z.string().optional(),
    /** Absent shows both kinds - the library's « Tout » chip. */
    kind: ambianceAssetKindSchema.optional(),
  })
  .strict();
export type AmbianceAssetQuery = z.infer<typeof ambianceAssetQuerySchema>;

/**
 * Fold case and accents so « lumiere » finds « lumière ».
 *
 * Written as a code-point walk rather than a regex range: a character class
 * spanning the combining marks is exactly what `noMisleadingCharacterClass`
 * refuses, and it is right to - the range is only safe here BECAUSE the string
 * was decomposed first, which a reader of the class alone cannot see.
 */
function fold(text: string): string {
  let folded = '';
  for (const character of text.toLowerCase().normalize('NFD')) {
    const code = character.codePointAt(0) ?? 0;
    // The combining diacritical marks block, which NFD has just split out.
    if (code >= 0x300 && code <= 0x36f) continue;
    folded += character;
  }
  return folded;
}

/**
 * The library's narrowing, PURE and shared by both sides of the wire: the
 * route filters with it, the picker filters what it already holds with it.
 * Two implementations of "does this file match what was typed" would answer
 * differently on the first accent, and the operator would blame the file.
 *
 * Every word must match somewhere - filename, tag or license - so typing two
 * words narrows instead of widening.
 */
export function filterAmbianceAssets(
  assets: readonly AmbianceAsset[],
  query: AmbianceAssetQuery,
): AmbianceAsset[] {
  const words = fold(query.q ?? '')
    .split(/\s+/)
    .filter((word) => word !== '');
  return assets.filter((asset) => {
    if (query.kind !== undefined && asset.kind !== query.kind) return false;
    const haystack = fold([asset.filename, ...asset.tags, asset.license].join(' '));
    return words.every((word) => haystack.includes(word));
  });
}

/**
 * What `GET /api/ambiance/assets` serves: the library alone, narrowed. A
 * SECOND route rather than a parameter on the view, because the two answer
 * two different questions - the view is « what plays behind every slot », the
 * library is « what files do I have » - and the media picker of BUILD-831 has
 * no use for the slots or the journal.
 */
export const ambianceAssetListSchema = z.object({ assets: z.array(ambianceAssetSchema) }).strict();
export type AmbianceAssetList = z.infer<typeof ambianceAssetListSchema>;

/**
 * Why an ambiance write was refused. The screen words every refusal in French
 * FROM THE CODE (composer rule §11.2) - the server `message` is a developer's
 * string. Adding a code here without its wording in the frontend catalogue
 * stops compiling, which is the point of the enum.
 *
 * `LICENSE_REQUIRED` left this enumeration in BUILD-891: the license is
 * facultative on both natures now, so no path raises it any more, and keeping
 * its French sentence would describe a rule that no longer exists. The four
 * slot and assignment codes left it in #32 for the same reason: the library
 * has no slots to refuse against, and an unknown id is a 404 rather than a
 * worded refusal.
 */
export const ambianceErrorCodeSchema = z.enum([
  'FILE_REQUIRED',
  'UNSUPPORTED_FORMAT',
  'FILE_TOO_LARGE',
  'ASSET_IN_USE',
]);
export type AmbianceErrorCode = z.infer<typeof ambianceErrorCodeSchema>;

/* ------------------------------------------------ the file that cannot go */

/**
 * One referent of a file the library is asked to delete (refonte modulaire
 * §9.1): an experience whose TAGGED version cites it, with the tags it cites it
 * under - « Accueil PMS (production, développement) ».
 *
 * `kind` is a literal and not the versioned enum, and that is deliberate: an
 * experience is the only object that cites a file, because a file is only ever
 * put on a page or behind a theme. The day another one does, this becomes a
 * union and every reader is told by the compiler.
 */
export const ambianceAssetReferentSchema = z
  .object({
    kind: z.literal('experience'),
    id: z.string().min(1),
    name: z.string().min(1),
    tags: z.array(versionTagSchema).min(1),
  })
  .strict();
export type AmbianceAssetReferent = z.infer<typeof ambianceAssetReferentSchema>;

/**
 * The 409 of a deletion refused (§9.1).
 *
 * The media library is NOT versioned and its deletion is HARD: the file goes.
 * A retired criterion breaks no session in flight - its content is frozen in
 * the version - where a deleted file breaks a page that is being played at that
 * instant. So the rule of §4, « le retrait reste toujours possible », does not
 * reach here: it holds for versioned objects, and a file is not one.
 *
 * What is refused is the deletion of a file cited by a TAGGED version. A file
 * cited only by a draft, or by a historical version no pointer sits on, is
 * deleted: the draft then shows « fichier supprimé, à remplacer » and
 * « Enregistrer » refuses until it is.
 */
export const ambianceAssetInUseSchema = z
  .object({
    error: z.string().min(1),
    code: z.literal('ASSET_IN_USE'),
    referents: z.array(ambianceAssetReferentSchema),
  })
  .strict();
export type AmbianceAssetInUse = z.infer<typeof ambianceAssetInUseSchema>;
